import { Navigate, Outlet, useLocation } from 'react-router';

import { hasPermission } from '@octbill/shared/permissions';

import { useAuth } from '../auth.jsx';
import { EmptyState, Loading } from './ui.jsx';

/** Client-area pages: signed-in clients only; others go to /login and come back after. */
export function RequireClient() {
  const { me, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <Loading />;
  if (!me?.client) return <Navigate to={`/login?next=${encodeURIComponent(pathname + search)}`} replace />;
  return <Outlet />;
}

/** Admin pages: signed-in staff only; others go to /admin/login. */
export function RequireStaff() {
  const { me, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <Loading />;
  if (!me?.staff) return <Navigate to={`/admin/login?next=${encodeURIComponent(pathname + search)}`} replace />;
  return <Outlet />;
}

/** Whether the signed-in staff member has a permission (wildcards included). */
export function useCan() {
  const { me } = useAuth();
  return (permission) => Boolean(me?.staff) && (!permission || hasPermission(me.staff.permissions, permission));
}

/** Shows `children` only with the permission; otherwise a short explanation (or nothing with `quiet`). */
export function Can({ permission, children, quiet = false }) {
  const can = useCan();
  if (can(permission)) return children;
  if (quiet) return null;
  return (
    <EmptyState title="You don't have access to this">Ask an administrator for the {permission} permission.</EmptyState>
  );
}
